import websiteContext from './websiteContext';
import { logError } from './utilities/logger';
import { getQueryParameter } from './utilities/url';

export const SERVICE_CLIENT_ID = 'upload-design-page';

/* enumerates the attributes which constitute the "shape" of a product
 * e.g. for business cards we list
 *   CornerShape (rounded/square)
 *   Orientation (horizontal/vertical)
 *   Shape (rectangle/square)
 * these are used to compute every possible "shape" available for a given MP
 */
const shapeAttributeIds: Set<string> = new Set();
// IE11 doesn't support new Set(iterable), so we have to do this forEach
[
  'Attribute-233885', // Corners (e.g. rounded, standard)
  'Attribute-233893', // BC Size (e.g. standard, square, slim)
  'ProductOrientation', // Orientation (e.g. vertical, horizontal)
].forEach(attributeId => {
  shapeAttributeIds.add(attributeId);
});

/**
 * Whether or not the given attributeId affects the product's "shape"
 * @param attributeId
 */
export function isShapeAttribute(attributeId: string) {
  return shapeAttributeIds.has(attributeId);
}

/* identifies a unique product "shape"
 * used to configure ProductShape specific settings such as "Display Name"
 * see services/productNames.ts
 *
 * From an implementation standpoint,
 * the id is generated by lexicographically sorting the minimum set of attribute choiceIds that describe the shape
 * e.g. Standard Bussiness Card shape is comprised of 3 attributes:
 *   Size = 233847 // rectangle
 *   Corners = 233838 // rounded
 *   Orientation = 106926 // horizontal
 * so it's ProductShapeId is "106926,233838,233847"
 */
export enum ProductShapeIds {
  NA_STANDARD_BUSINESS_CARD = '106926,233838,233847',
  NA_VERTICAL_STANDARD_BUSINESS_CARD = '106927,233838,233847',
  NA_ROUNDED_CORNER_BUSINESS_CARD = '106926,233840,233847',
  NA_VERTICAL_ROUNDED_CORNER_BUSINESS_CARD = '106927,233840,233847',
  NA_SQUARE_BUSINESS_CARD = '106926,233838,233850',

  EU_STANDARD_BUSINESS_CARD = '106926,233838,238784',
  EU_VERTICAL_STANDARD_BUSINESS_CARD = '106927,233838,238784',
  EU_ROUNDED_CORNER_BUSINESS_CARD = '106926,233840,238784',
  EU_VERTICAL_ROUNDED_CORNER_BUSINESS_CARD = '106927,233840,238784',
  EU_SQUARE_BUSINESS_CARD = '106926,233838,238792',
  EU_SLIM_BUSINESS_CARD = '106926,233838,238793',
  EU_VERTICAL_SLIM_BUSINESS_CARD = '106927,233838,238793',
}

export function convertToProductShapeId(choiceIds: string[]) {
  return choiceIds.sort().join(',');
}

// NewRelic
let newRelicLoggingEnabled = false;
export function isNewRelicLoggingEnabled() {
  return newRelicLoggingEnabled;
}

/**
 * This data will be added to all PageView and Error events
 */
export function getNewRelicCustomData(): { [key: string]: string | number } {
  const merchProductId = getQueryParameter('mp_id');
  const projectId = getQueryParameter('project_id');

  let identifierType = 'NONE';
  if (projectId !== null) {
    identifierType = 'PROJECT_ID';
  } else if (merchProductId !== null) {
    identifierType = 'MP_ID';
  }

  return {
    identifierType,
    projectId: projectId || 'NONE',
    mpId: merchProductId || 'NONE',
    // websiteContext.getLanguage() will default to en-us if utag_data doesn't exist
    // but those sessions aren't necessarily actual en-us sessions, so we'll mark them as unknown
    websiteLanguage: window.utag_data !== undefined ? websiteContext.getLanguage() : 'UNKNOWN',
  };
}

export function configureNewRelic() {
  try {
    newrelic.addRelease('upload-page', process.env.REACT_APP_VERSION || 'UNKNOWN');

    const customData = getNewRelicCustomData();
    Object.entries(customData).forEach(([key, value]) => {
      // this adds data to the PageView events (but not the Error events)
      newrelic.setCustomAttribute(key, value);
    });

    // enable newRelicLogging
    newRelicLoggingEnabled = true;
  } catch (error) {
    logError(error, 'Failed to configure newrelic');
  }
}
